import { useRef, useState } from "react";
import { Loader2, Map, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const successToast = (data, tid) => {
  const extras = [];
  if (data.prefill?.start_date) extras.push("datums gevonden");
  const hotels = (data.prefill?.planned_stays || []).concat(data.prefill?.planned_stays_return || []).filter((p) => p.name).length + (data.prefill?.destination_stay_name ? 1 : 0);
  if (hotels) extras.push(`${hotels} ${hotels === 1 ? "verblijfsnaam" : "verblijfsnamen"} gevonden`);
  toast.success(`Reisschema herkend (zekerheid: ${data.confidence})${extras.length ? ` — ${extras.join(", ")}` : ""}${data.notes ? ` — ${data.notes}` : ""}`, { id: tid, duration: 8000 });
};

export const MapPhotoImport = ({ onResult, className = "" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [text, setText] = useState("");
  const [textBusy, setTextBusy] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const tid = toast.loading("Foto analyseren met AI — dit duurt een paar seconden...");
    try {
      const small = await compressImage(f, 1800, 0.85).catch(() => f);
      const fd = new FormData();
      fd.append("file", small, "kaart.jpg");
      const { data } = await api.post("/import/map-photo", fd, { timeout: 120000 });
      successToast(data, tid);
      onResult(data.prefill);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kon de kaart niet lezen", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const analyzeText = async () => {
    if (text.trim().length < 10) { toast.error("Plak eerst je reisschema — minstens een paar regels"); return; }
    setTextBusy(true);
    const tid = toast.loading("Schema analyseren met AI...");
    try {
      const { data } = await api.post("/import/schema-text", { text }, { timeout: 120000 });
      successToast(data, tid);
      onResult(data.prefill);
      setTextOpen(false);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kon het schema niet lezen", { id: tid });
    } finally {
      setTextBusy(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="map-photo-import-input" />
      <div className={`flex items-center gap-2 flex-wrap ${className}`}>
        <button type="button" data-testid="map-photo-import-button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-primary/50 hover:border-primary text-primary text-sm font-semibold px-4 py-2 transition-[border-color,background-color] duration-200 hover:bg-primary/5 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Map className="w-4 h-4" />}
          {busy ? "Foto analyseren..." : "Foto uploaden"}
        </button>
        <button type="button" data-testid="schema-text-import-button" onClick={() => setTextOpen(true)} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-primary/50 hover:border-primary text-primary text-sm font-semibold px-4 py-2 transition-[border-color,background-color] duration-200 hover:bg-primary/5 disabled:opacity-60">
          <ClipboardPaste className="w-4 h-4" /> Tekst plakken
        </button>
      </div>
      <Dialog open={textOpen} onOpenChange={(o) => !textBusy && setTextOpen(o)}>
        <DialogContent className="rounded-2xl sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Plak je reisschema</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Kopieer je dagschema of tabel (uit een mail, blog, Excel of notitie) en plak het hieronder. AI vult vertrek, bestemming, stops, datums en verblijfsnamen vooraf in.
          </p>
          <Textarea data-testid="schema-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={9}
            placeholder={"bv.\nza 12 juli: vertrek Gent → Reims\nzo 13 juli: Reims → Annecy (Hotel du Lac)\nma 14-15 juli: Annecy\ndi 16 juli: Annecy → Nice ..."}
            className="rounded-xl font-mono text-xs" />
          <Button data-testid="schema-text-analyze-button" onClick={analyzeText} disabled={textBusy}
            className="rounded-full w-full active:scale-95 transition-[transform,background-color] duration-200">
            {textBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyseren...</> : "Analyseer met AI"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
